import { z } from 'zod';

/**
 * Structured document (ADR-030): a vendor-neutral view of a page's structure for consumers that
 * need more than plain text — tables with header cells, checklist items with their state,
 * macros/components with parameters, links to pages/issues/attachments. Adapters map their
 * native format to it; domain packs interpret it. Every block carries a stable `locator`
 * ("b3", "b3.r1.c2", "b7.m.b0") used as SourceRef.locator.anchor for citations.
 */
export const DocLink = z.object({
  kind: z.enum(['page', 'issue', 'url', 'attachment', 'user', 'space']),
  /** Page title/id, issue key, URL, attachment file name, user reference or space key. */
  target: z.string().max(2000),
  /** Space of a page link when known. */
  space: z.string().max(200).optional(),
  text: z.string().max(2000),
});
export type DocLink = z.infer<typeof DocLink>;

export type DocBlock =
  | { type: 'heading'; locator: string; level: number; text: string }
  | { type: 'paragraph'; locator: string; text: string; links: DocLink[] }
  | { type: 'list'; locator: string; ordered: boolean; items: DocListItem[] }
  | { type: 'task'; locator: string; done: boolean; text: string }
  | { type: 'table'; locator: string; rows: DocTableRow[] }
  | {
      type: 'macro';
      locator: string;
      /** Native component name, e.g. "panel", "expand", "jira", "include". */
      name: string;
      params: Record<string, string>;
      links: DocLink[];
      body: DocBlock[];
    }
  | { type: 'code'; locator: string; language: string | null; text: string }
  | { type: 'divider'; locator: string };

export interface DocListItem {
  text: string;
  links: DocLink[];
  children: DocBlock[];
}
export interface DocTableCell {
  locator: string;
  header: boolean;
  colspan: number;
  /** Plain text of the whole cell (including nested content). */
  text: string;
  links: DocLink[];
  blocks: DocBlock[];
}
export interface DocTableRow {
  cells: DocTableCell[];
}

export const DocBlock: z.ZodType<DocBlock> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('heading'),
      locator: z.string(),
      level: z.number().int().min(1).max(6),
      text: z.string(),
    }),
    z.object({
      type: z.literal('paragraph'),
      locator: z.string(),
      text: z.string(),
      links: z.array(DocLink),
    }),
    z.object({
      type: z.literal('list'),
      locator: z.string(),
      ordered: z.boolean(),
      items: z.array(
        z.object({ text: z.string(), links: z.array(DocLink), children: z.array(DocBlock) }),
      ),
    }),
    z.object({
      type: z.literal('task'),
      locator: z.string(),
      done: z.boolean(),
      text: z.string(),
    }),
    z.object({
      type: z.literal('table'),
      locator: z.string(),
      rows: z.array(
        z.object({
          cells: z.array(
            z.object({
              locator: z.string(),
              header: z.boolean(),
              colspan: z.number().int().min(1),
              text: z.string(),
              links: z.array(DocLink),
              blocks: z.array(DocBlock),
            }),
          ),
        }),
      ),
    }),
    z.object({
      type: z.literal('macro'),
      locator: z.string(),
      name: z.string(),
      params: z.record(z.string(), z.string()),
      links: z.array(DocLink),
      body: z.array(DocBlock),
    }),
    z.object({
      type: z.literal('code'),
      locator: z.string(),
      language: z.string().nullable(),
      text: z.string(),
    }),
    z.object({ type: z.literal('divider'), locator: z.string() }),
  ]),
);

export const StructuredDocument = z.object({
  format: z.literal('structured-document@1'),
  blocks: z.array(DocBlock),
  /** True when the adapter stopped early because of its size limits. */
  truncated: z.boolean(),
});
export type StructuredDocument = z.infer<typeof StructuredDocument>;

/** Depth-first walk over every block (including table cells, list items and macro bodies). */
export function* walkBlocks(blocks: readonly DocBlock[]): Generator<DocBlock> {
  for (const b of blocks) {
    yield b;
    if (b.type === 'table') {
      for (const r of b.rows) for (const c of r.cells) yield* walkBlocks(c.blocks);
    } else if (b.type === 'macro') {
      yield* walkBlocks(b.body);
    } else if (b.type === 'list') {
      for (const i of b.items) yield* walkBlocks(i.children);
    }
  }
}
